import { UserFriendlyError } from '@attain-apps/errors'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjsUtils from '@attain-apps/rxjs-utils'
import * as rxjs from 'rxjs'

import type * as admin from '../../../admin'
import * as common from '../../../common'
import type * as resources from '../../resources'
import type { Feature } from '../feature'
import * as gql from '../gql'

/**
 * Executes a Quiver evaluation request
 */
export const executeEvaluate = store.effects.create(
  (
    feature: Feature,
    contextsFeature: admin.contexts.Feature,
    resourcesEc: resources.Collection,
  ) =>
    feature.actions.evaluate.request.observe().pipe(
      rxjsUtils.latestFrom(request => ({
        request: rxjs.of(request),
        resourceIds:
          resourcesEc.plugins.selectionPlugin.feature.selectors.getAllSelectedIds()
            .asObservable,
        variables: feature.selectors.variableValues.asObservable,
        zoneContext: contextsFeature.selectors.zoneContext.asObservable,
      })),
      rxjs.switchMap(({ request, resourceIds, variables, zoneContext }) => {
        // Validating parameters
        if (!zoneContext) {
          return rxjs.of(
            feature.actions.evaluate.cancellation.from(request).create({
              reason: 'Cannot proceed without a current tenant & zone',
              request: request.data,
            }),
          )
        }
        if (!resourceIds.length) {
          return rxjs.of(
            feature.actions.evaluate.cancellation.from(request).create({
              reason: 'No selected resources to evaluate',
              request: request.data,
            }),
          )
        }
        // Executing
        return gql.queries.evaluate
          .execute({
            ...zoneContext,
            resourceIds,
            variables:
              common.gql.dms.data.inputs.variableValue.TRANSFORMER.getGqlFrom(
                variables,
              ),
          })
          .pipe(
            // Processing response
            rxjs.map(evaluation =>
              evaluation
                ? feature.actions.evaluate.success.from(request).create({
                    request: request.data,
                    response: {
                      evaluation,
                    },
                  })
                : feature.actions.evaluate.error.from(request).create({
                    error: new UserFriendlyError(
                      'Quiver deployment evaluation produced an unexpected result',
                    ),
                    request: request.data,
                  }),
            ),
            // Handling error
            rxjs.catchError((error: Error) =>
              rxjs.of(
                feature.actions.evaluate.error.from(request).create({
                  error,
                  request: request.data,
                }),
              ),
            ),
          )
      }),
    ),
)
